import { ChangeEvent, Dispatch, ReactElement, SetStateAction, useState } from "react";
import { Pencil, Plus } from "lucide-react";
import { ControllerProps, useForm } from "react-hook-form";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/_atoms/alert-dialog";
import { Button } from "@/components/_atoms/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/_atoms/form";
import { Input } from "@/components/_atoms/input";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { useMunicipalPropertyFormSchema } from "@/domain/municipal-property/hooks/use-municipal-property-form-schema";
import { MunicipalPropertyCatalogRow } from "@/domain/municipal-property/models/municipal-property-catalog-row.model";
import { MunicipalPropertyCreateFormValues } from "@/domain/municipal-property/models/municipal-property-create-form-values";
import { MunicipalPropertyStandSubmission } from "@/domain/municipal-property/models/municipal-property-stand-submission.model";
import { toMunicipalPropertyFormValues } from "@/domain/municipal-property/models/to-municipal-property-form-values";
import { WorkspaceMunicipalityOption } from "@/domain/municipal-property/models/workspace-municipality-option.model";
import { extractGraphQLErrorCode } from "@/lib/extract-graphql-error-code";
import { MUNICIPAL_PROPERTY_MASTER_DATA_BOUNDS } from "@ark/shared";
import { zodResolver } from "@hookform/resolvers/zod";

import { MunicipalPropertyMasterDataFields } from "./municipal-property-master-data-fields";
import { MunicipalPropertyMunicipalityConflictDialog } from "./municipal-property-municipality-conflict-dialog";
import { MunicipalPropertyMunicipalityFieldset } from "./municipal-property-municipality-fieldset";

interface MunicipalPropertyStandFormProps {
  municipalityList: readonly WorkspaceMunicipalityOption[];
  property: MunicipalPropertyCatalogRow;
  initialEffectiveYear: number;
  overwriteEffectiveYear: number;
  isUpdating: boolean;
  onSubmit: (input: MunicipalPropertyStandSubmission) => Promise<void>;
  onSuccess: () => void;
  onCancel: () => void;
}
export function MunicipalPropertyStandForm({
  municipalityList,
  property,
  initialEffectiveYear,
  overwriteEffectiveYear,
  isUpdating,
  onSubmit,
  onSuccess,
  onCancel,
}: MunicipalPropertyStandFormProps): ReactElement {
  const { t }: ReturnType<typeof useDomainTranslation> = useDomainTranslation("municipal-property");

  const [pendingOverwrite, setPendingOverwrite]: [
    MunicipalPropertyCreateFormValues | null,
    Dispatch<SetStateAction<MunicipalPropertyCreateFormValues | null>>,
  ] = useState<MunicipalPropertyCreateFormValues | null>(null);
  const [editMode, setEditMode]: ["new" | "overwrite", Dispatch<SetStateAction<"new" | "overwrite">>] = useState<
    "new" | "overwrite"
  >("new");
  const [newEffectiveYear, setNewEffectiveYear]: [number, Dispatch<SetStateAction<number>>] =
    useState(initialEffectiveYear);
  const [municipalityError, setMunicipalityError] = useState<unknown>(null);
  const schema: ReturnType<typeof useMunicipalPropertyFormSchema> = useMunicipalPropertyFormSchema();

  const form: ReturnType<typeof useForm<MunicipalPropertyCreateFormValues>> =
    useForm<MunicipalPropertyCreateFormValues>({
      resolver: zodResolver(schema),
      defaultValues: toMunicipalPropertyFormValues({ property, effectiveYear: initialEffectiveYear }),
    });

  function handleEditModeChange(value: string): void {
    if (value === editMode) return;
    if (value === "overwrite") {
      setNewEffectiveYear(form.getValues("effectiveYear"));
      form.setValue("effectiveYear", overwriteEffectiveYear);
    } else {
      form.setValue("effectiveYear", newEffectiveYear);
    }
    setEditMode(value === "overwrite" ? "overwrite" : "new");
    form.clearErrors("effectiveYear");
  }

  async function handleSubmit({
    values,
    isOverwrite = false,
  }: {
    values: MunicipalPropertyCreateFormValues;
    isOverwrite?: boolean;
  }): Promise<void> {
    try {
      await onSubmit({ values, isOverwrite });
      toast.success(t("edit.success"));
      onSuccess();
    } catch (error) {
      if (extractGraphQLErrorCode(error) === "MUNICIPAL_PROPERTY_MUNICIPALITY_CONFLICT") {
        setMunicipalityError(error);
        return;
      }
      toast.error(t(extractGraphQLErrorCode(error) === "CONFLICT" ? "history.conflict" : "edit.error"));
    }
  }

  function handleValidatedSubmit(values: MunicipalPropertyCreateFormValues): void | Promise<void> {
    if (editMode === "overwrite") {
      setPendingOverwrite({ ...values, effectiveYear: overwriteEffectiveYear });
    } else {
      return handleSubmit({ values });
    }
  }

  function handleConfirmOverwrite(): void {
    if (pendingOverwrite) void handleSubmit({ values: pendingOverwrite, isOverwrite: true });
    setPendingOverwrite(null);
  }

  return (
    <Form {...form}>
      <MunicipalPropertyMunicipalityConflictDialog
        error={municipalityError}
        onClose={() => setMunicipalityError(null)}
      />
      <form
        noValidate
        onSubmit={form.handleSubmit(handleValidatedSubmit)}
        className="flex flex-col gap-5"
        data-testid="municipal-property-stand-form"
      >
        <fieldset disabled={isUpdating || form.formState.isSubmitting} className="flex min-w-0 flex-col gap-6">
          <header>
            <div role="group" aria-label={t("history.editAction")} className="flex flex-wrap gap-2">
              <Button
                variant={editMode === "new" ? "default" : "outline"}
                aria-pressed={editMode === "new"}
                onClick={() => handleEditModeChange("new")}
                className="h-auto min-h-10 gap-2 whitespace-normal text-left"
              >
                <Plus className="size-4" aria-hidden="true" />
                {t("history.newStand")}
              </Button>
              <Button
                variant={editMode === "overwrite" ? "default" : "ghost"}
                aria-pressed={editMode === "overwrite"}
                onClick={() => handleEditModeChange("overwrite")}
                className="h-auto min-h-10 gap-2 whitespace-normal text-left"
              >
                <Pencil className="size-4" aria-hidden="true" />
                {t("history.correctStand", { year: overwriteEffectiveYear })}
              </Button>
            </div>
          </header>
          {editMode === "new" ? (
            <FormField
              control={form.control}
              name="effectiveYear"
              render={({
                field,
              }: Parameters<
                ControllerProps<MunicipalPropertyCreateFormValues, "effectiveYear">["render"]
              >[0]): ReactElement => (
                <FormItem className="rounded-md border bg-muted/20 p-4">
                  <FormLabel>{t("history.newEffectiveYear")}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={MUNICIPAL_PROPERTY_MASTER_DATA_BOUNDS.minEffectiveYear}
                      max={MUNICIPAL_PROPERTY_MASTER_DATA_BOUNDS.maxEffectiveYear}
                      {...field}
                      onChange={(event: ChangeEvent<HTMLInputElement>): void =>
                        field.onChange(Number(event.target.value))
                      }
                    />
                  </FormControl>
                  <FormDescription>{t("history.templateHint", { year: overwriteEffectiveYear })}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : (
            <p className="rounded-md bg-muted/30 p-3 text-sm" role="status">
              {t("history.overwriteTarget", { year: overwriteEffectiveYear })}
            </p>
          )}
          <div className="space-y-6 border-t pt-6 [&_legend]:mb-3 [&_legend]:text-base [&_legend]:font-semibold">
            <MunicipalPropertyMasterDataFields control={form.control} />
            <MunicipalPropertyMunicipalityFieldset control={form.control} municipalityList={municipalityList} />
          </div>

          <div className="flex flex-wrap justify-end gap-2 border-t pt-4">
            <Button type="button" variant="ghost" onClick={onCancel}>
              {t("create.cancel")}
            </Button>
            <Button type="submit" disabled={isUpdating || form.formState.isSubmitting}>
              {isUpdating
                ? t("edit.submitting")
                : editMode === "overwrite"
                  ? t("history.reviewOverwrite", { year: overwriteEffectiveYear })
                  : t("history.createStand", { year: form.watch("effectiveYear") })}
            </Button>
          </div>
        </fieldset>
        <AlertDialog
          open={!!pendingOverwrite}
          onOpenChange={(isOpen: boolean): void => {
            if (!isOpen) setPendingOverwrite(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("history.overwriteTitle", { name: property.name, year: pendingOverwrite?.effectiveYear })}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("history.overwriteDescription", { year: pendingOverwrite?.effectiveYear })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={(): void => setPendingOverwrite(null)}>
                {t("create.cancel")}
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmOverwrite}>{t("history.overwrite")}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </form>
    </Form>
  );
}
